import publicMethod from '@/utils/publicMethod';
import { Col, DatePicker, Input, message, Popover, Row, Spin } from 'antd';
import dayjs from 'dayjs';
import { useState, useCallback, useEffect } from 'react';
import debounce from 'lodash.debounce';
import { calendarIcon, searchIcon } from '@/constants/static';
import Pagination from '@/components/pagination';
import AssignTaskerModal from './AssignTaskerModal';
import TaskLogModal from './TaskLogModal';
import './modal.scss';
import taskApi from '@/api/task';
import format from '@/utils/format';
import { useNavigate } from 'react-router-dom';
import CancelTaskModal from './CancelTaskModal';
import { useActivate } from 'react-activation';
interface TaskItem {
    taskId: string;
    taskTitle: string;
    taskDescription: string;
    categoryName: string;
    customerFullName: string | null;
    taskerFullName: string | null;
    workStartTime: string | null;
    workEndTime: string | null;
    taskPublishTime: string;
    asap: boolean;
    actualAmountEarnedByTasker: number | null;
    actualPaymentAmount: string | null;
    actualRemunerationAmount: number | null;
    minPaymentAmount: string;
    maxPaymentAmount: string;
    customerId: string;
    status: string;
    canceledBy: string;
    receiveTime: string;
    workSuburb: string;
    workState: string;
    postcode: string;
}
const Tasks = () => {
    const link = useNavigate();
    const { RangePicker } = DatePicker;
    const [listParams, setListParams] = useState({
        startTime: '',
        endTime: '',
        keyword: '',
        pageNum: 1,
        taskState: 'POSTED_NOT_ASSIGNED',
    });
    const dateOnChange = date => {
        setListParams({
            ...listParams,
            startTime: date ? dayjs(`${dayjs(date[0]).format('YYYY-MM-DD')} 00:00:00`).toISOString() : '',
            endTime: date ? dayjs(`${dayjs(date[1]).format('YYYY-MM-DD')} 23:59:59`).toISOString() : '',
            pageNum: 1,
        });
    };
    const inputChange = e => {
        debouncedSave({ ...listParams, keyword: e.target.value, pageNum: 1 });
    };
    const debouncedSave = useCallback(
        debounce(nextValue => setListParams(nextValue), 1500),
        []
    );
    const [tabKey, setTabKey] = useState(0);
    const tableTabs = [
        { label: 'Posted, Not assigned', value: 'POSTED_NOT_ASSIGNED', total: 'postedNotAssigned' },
        { label: 'Active tasks', value: 'ACTIVE_TASKS', total: 'activeTasks' },
        { label: 'Completed tasks', value: 'COMPLETE_TASKS', total: 'completeTasks' },
        { label: 'Cancelled tasks', value: 'CANCELLED_TASKS', total: 'cancelledTasks' },
    ];
    const handlePageChange = prop => {
        setListParams(state => ({ ...state, pageNum: prop }));
    };
    const [loading, setLoading] = useState(true);
    const [taskData, setTaskData] = useState<{
        total: number;
        metaData: {
            activeTasks: number;
            cancelledTasks: number;
            completeTasks: number;
            postedNotAssigned: number;
        };
        list: TaskItem[];
    }>({
        metaData: {
            activeTasks: 0,
            cancelledTasks: 0,
            completeTasks: 0,
            postedNotAssigned: 0,
        },
        total: 0,
        list: [],
    });
    async function getTasks() {
        setLoading(true);
        try {
            const res = await taskApi.getTaskList({ ...listParams, pageSize: 10 });
            const { metaData, records, total } = res.data;
            setTaskData({ metaData, list: records, total: Number(total) });
            sessionStorage.removeItem('getTasks');
        } catch (error: any) {
            message.error(error);
            setLoading(false);
        } finally {
            setLoading(false);
        }
    }
    useEffect(() => {
        getTasks();
    }, [listParams]);
    const popupContainer = document.getElementById('select-bar') || document.body;
    const [currentTask, setCurrentTask] = useState<{
        taskId: string;
        workEndTime: string | null;
        type: '' | 'assign' | 'log' | 'cancel';
    }>({
        taskId: '',
        workEndTime: null,
        type: '',
    });
    const formatPublishTime = (second: string) => {
        const publishMinute = Number(second) / 60;
        return `Finding for ${
            publishMinute > 60 ? (publishMinute / 60).toFixed() + 'h' : publishMinute.toFixed() + 'min'
        }...`;
    };
    useActivate(() => {
        if (sessionStorage.getItem('getTasks')) {
            getTasks();
        }
    });
    const showAssign = (workStartTime: string | null) => {
        if (!workStartTime) return true;
        const startUnix = dayjs(workStartTime).unix();
        const nowUnix = dayjs().unix();
        console.log(startUnix - nowUnix);
        return startUnix - nowUnix > 1800;
    };
    return (
        <div className="page-main tasks">
            <div className="page-title">Marketplace Tasks</div>
            <div className="select-bar" id="select-bar">
                <RangePicker
                    className="search-input"
                    placeholder={['dd/mm/yyyy', 'dd/mm/yyyy']}
                    suffixIcon={calendarIcon}
                    separator={'-'}
                    format={'DD/MM/YYYY'}
                    disabledDate={current => current && current > dayjs().endOf('day').add(7, 'day')}
                    onChange={dateOnChange}
                    getPopupContainer={() => popupContainer}
                />
                <Input
                    className="search-input"
                    placeholder="Search by task"
                    onChange={inputChange}
                    prefix={searchIcon}
                />
            </div>
            <div className="table">
                <div className="tabs-table">
                    {tableTabs.map((t, index) => {
                        return (
                            <div
                                className={`tabs-table-item ${index === tabKey ? 'tabs-table-item-active' : ''}`}
                                key={t.value}
                                onClick={() => {
                                    setListParams(state => ({ ...state, taskState: t.value, pageNum: 1 }));
                                    setTabKey(index);
                                }}
                            >
                                {t.label} ({taskData.metaData[t.total]})
                            </div>
                        );
                    })}
                </div>
                {loading ? (
                    <div className="data_loading flex-center">
                        <Spin />
                    </div>
                ) : taskData.total ? (
                    <>
                        <div className="table-th">
                            <Row>
                                <Col span={tabKey ? 4 : 5}>Task</Col>
                                <Col span={3}>Customer</Col>
                                {tabKey > 0 && <Col span={3}>Tasker</Col>}
                                <Col span={3}>Task Info</Col>
                                <Col span={tabKey ? 3 : 4}>Task Value</Col>
                                <Col span={tabKey ? 3 : 4}>{tabKey && tabKey !== 3 ? 'Status' : 'Posted Date'}</Col>
                                <Col span={tabKey ? 3 : 4}>Task Date</Col>
                            </Row>
                        </div>
                        {taskData.list.map(i => {
                            return (
                                <Row className="table-tr" key={i.taskId}>
                                    <Col span={tabKey ? 4 : 5}>
                                        <div
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => {
                                                window.sessionStorage.setItem(
                                                    'taskInfo',
                                                    JSON.stringify({
                                                        userId: i.customerId,
                                                        taskId: i.taskId,
                                                    })
                                                );
                                                link(`/task/detail?type=${window.btoa('customer')}`);
                                            }}
                                        >
                                            {i.taskTitle}
                                            <div className="desc_tip two-line-text">{i.taskDescription}</div>
                                        </div>
                                    </Col>
                                    <Col span={3}>{i.customerFullName}</Col>
                                    {tabKey > 0 && <Col span={3}>{i.taskerFullName || '-'}</Col>}
                                    <Col span={3}>
                                        <div className="cate-tag">{i.categoryName}</div>
                                        <div>
                                            {i.workSuburb} {i.workState} {i.postcode}
                                        </div>
                                    </Col>
                                    <Col span={tabKey ? 3 : 4}>
                                        {tabKey
                                            ? i.actualPaymentAmount
                                                ? `$${publicMethod.formatPrice(i.actualPaymentAmount)}`
                                                : '-'
                                            : `$${publicMethod.formatPrice(
                                                  i.minPaymentAmount
                                              )}-$${publicMethod.formatPrice(i.maxPaymentAmount)}`}
                                    </Col>
                                    {/* tabKey ? 'Status' : 'Posted Date' */}
                                    <Col span={tabKey ? 3 : 4}>
                                        {tabKey && tabKey !== 3 ? (
                                            <span
                                                style={{
                                                    color: i.status === 'Paid' ? '#7DB8A4' : '#F76D20',
                                                }}
                                            >
                                                {i.status}
                                            </span>
                                        ) : (
                                            <>
                                                {format.formatTimeMDY(i.taskPublishTime)}{' '}
                                                {tabKey !== 3 && (
                                                    <div className="desc_tip" style={{ color: '#F76D20' }}>
                                                        {formatPublishTime(i.receiveTime)}
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </Col>
                                    <Col span={tabKey ? 3 : 4}>
                                        {i.asap && !i.workStartTime
                                            ? 'ASAP'
                                            : format.formatWorkPeriod(i.workStartTime, i.workEndTime)}
                                    </Col>
                                    <Col
                                        span={tabKey ? 2 : 1}
                                        style={{ display: 'flex', columnGap: 8, justifyContent: 'center' }}
                                    >
                                        {tabKey === 3 && (
                                            <div style={{ display: 'flex', columnGap: 5, lineHeight: '15px' }}>
                                                <img
                                                    width={15}
                                                    height={15}
                                                    src={require('@/assets/images/tip-trigger.png')}
                                                    alt=""
                                                />
                                                {i.canceledBy && (
                                                    <div style={{ opacity: '0.7' }}>
                                                        Cancelled by{' '}
                                                        <span
                                                            style={{ fontFamily: 'SemiBold' }}
                                                        >{`${i.canceledBy.replace(/^\w/, c => c.toUpperCase())}`}</span>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                        <Popover
                                            content={
                                                <div className="view-more">
                                                    {tabKey < 1 && (
                                                        <div
                                                            onClick={() =>
                                                                setCurrentTask({
                                                                    taskId: i.taskId,
                                                                    workEndTime: i.workEndTime,
                                                                    type: 'assign',
                                                                })
                                                            }
                                                        >
                                                            Assign a tasker
                                                        </div>
                                                    )}
                                                    <div
                                                        onClick={() =>
                                                            setCurrentTask({
                                                                taskId: i.taskId,
                                                                workEndTime: null,
                                                                type: 'log',
                                                            })
                                                        }
                                                    >
                                                        Task log
                                                    </div>
                                                    {tabKey < 2 && (
                                                        <div
                                                            onClick={() =>
                                                                setCurrentTask({
                                                                    taskId: i.taskId,
                                                                    workEndTime: null,
                                                                    type: 'cancel',
                                                                })
                                                            }
                                                        >
                                                            Cancel Job
                                                        </div>
                                                    )}
                                                </div>
                                            }
                                            placement="bottomRight"
                                        >
                                            <img
                                                style={{ cursor: 'pointer' }}
                                                src={require('@/assets/images/view-more.png')}
                                                alt="view-more"
                                                className="img-s20"
                                            />
                                        </Popover>
                                    </Col>
                                </Row>
                            );
                        })}
                    </>
                ) : (
                    <div className="no-data flex-center">No data</div>
                )}
                {taskData.total > 10 && (
                    <Pagination total={taskData.total} pageChange={handlePageChange} pageNo={listParams.pageNum} />
                )}
            </div>
            <AssignTaskerModal
                taskId={currentTask.taskId && currentTask.type === 'assign' ? currentTask.taskId : ''}
                workEndTime={currentTask.workEndTime}
                onCancel={() => setCurrentTask({ taskId: '', workEndTime: null, type: '' })}
            />
            <TaskLogModal
                taskId={currentTask.taskId && currentTask.type === 'log' ? currentTask.taskId : ''}
                onCancel={() => setCurrentTask({ taskId: '', workEndTime: null, type: '' })}
            />
            <CancelTaskModal
                taskId={currentTask.taskId && currentTask.type === 'cancel' ? currentTask.taskId : ''}
                onCancel={cancel => {
                    cancel && getTasks();
                    setCurrentTask({ taskId: '', workEndTime: null, type: '' });
                }}
            />
        </div>
    );
};
export default Tasks;
